import React, { useState } from 'react';
import {Button, Group, Radio, ScrollArea, Stack, Text, Title} from '@mantine/core';
import SCModal from '@/PageComponents/Modal/SCModal';

// Opens when the current primary / accounting contact's role box is unticked: a role always has
// one holder, so another active contact has to take it over. Cancelling keeps the role (the box
// is ticked again); the chosen contact takes over when the contact is saved.
type Props = {
  contact: any;
  roleName: string; // "primary" or "accounting"
  ownerName: string; // "customer" or "supplier"
  candidates: any[];
  onConfirm: (replacement: any) => void;
  onCancel: () => void;
};

const fullName = (x: any) => x?.FullName || [x?.FirstName, x?.LastName].filter(Boolean).join(' ');
const describe = (x: any) => [fullName(x), x.EmailAddress || x.MobileNumber].filter(Boolean).join(' - ');

const ContactRoleReplacementModal = ({ contact, roleName, ownerName, candidates, onConfirm, onCancel }: Props) => {
  const [selectedId, setSelectedId] = useState<string | null>(candidates.length === 1 ? candidates[0].ID : null);
  const replacement = candidates.find((x) => x.ID === selectedId) ?? null;

  return (
    <SCModal open={true} decor={'none'} onClose={onCancel} size={'md'}>
      <Stack gap={'md'} data-testid={'contact-role-replacement-modal'}>
        <Title size={'md'} fw={600}>Select new {roleName} contact</Title>
        <Text size={'sm'}>
          A {ownerName} always has a {roleName} contact. Select which contact should become the {roleName} contact
          instead of {fullName(contact)}. The change is made when you save.
        </Text>
        <ScrollArea.Autosize mah={'clamp(200px, 50vh, 400px)'}>
          <Radio.Group value={selectedId} onChange={setSelectedId} aria-label={`New ${roleName} contact`}>
            <Stack gap={'xs'}>
              {candidates.map((x) => (
                <Radio key={x.ID} value={x.ID as string} label={describe(x)} color={'scBlue'} />
              ))}
            </Stack>
          </Radio.Group>
        </ScrollArea.Autosize>
        <Group mt={'lg'} justify={'right'} gap={'xs'}>
          <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={onCancel}>
            Cancel
          </Button>
          <Button color={'scBlue'} disabled={!replacement} onClick={() => replacement && onConfirm(replacement)}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </SCModal>
  );
};

export default ContactRoleReplacementModal;
